import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Car,
  FileSpreadsheet,
  FileText,
  Printer,
  Search,
  RefreshCw,
  DollarSign
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function VehiclesReportPage() {
  const { API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState(new Set()); // IDs dos veículos selecionados
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [marcaFilter, setMarcaFilter] = useState('');
  const [modeloFilter, setModeloFilter] = useState('');
  const [anoFilter, setAnoFilter] = useState('');
  const [corFilter, setCorFilter] = useState('');
  const [ocultarVendidos, setOcultarVendidos] = useState(true);

  // Options for filters
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [anos, setAnos] = useState([]);
  const [cores, setCores] = useState([]);

  useEffect(() => {
    loadVehicles();
  }, []);

  useEffect(() => {
    filterVehicles();
    extractFilterOptions();
  }, [vehicles, searchTerm, statusFilter, marcaFilter, modeloFilter, anoFilter, corFilter, ocultarVendidos]);

  const loadVehicles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vehicles`, { withCredentials: true });
      setVehicles(response.data);
    } catch (error) {
      toast.error('Erro ao carregar veículos');
    } finally {
      setLoading(false);
    }
  };

  const extractFilterOptions = () => {
    const uniqueMarcas = [...new Set(vehicles.map(v => v.marca).filter(Boolean))];
    const uniqueModelos = [...new Set(vehicles.map(v => v.modelo).filter(Boolean))];
    const uniqueAnos = [...new Set(vehicles.map(v => v.ano_modelo).filter(Boolean))].sort((a, b) => b - a);
    const uniqueCores = [...new Set(vehicles.map(v => v.cor).filter(Boolean))];
    
    setMarcas(uniqueMarcas.sort());
    setModelos(uniqueModelos.sort());
    setAnos(uniqueAnos);
    setCores(uniqueCores.sort());
  };

  const filterVehicles = () => {
    let result = [...vehicles];

    // Hide sold vehicles
    if (ocultarVendidos) {
      result = result.filter(v => v.status !== 'vendido' && v.status !== 'roubado');
    }

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(v => 
        v.vehicle_id?.toLowerCase().includes(term) ||
        v.placa?.toLowerCase().includes(term) ||
        v.marca?.toLowerCase().includes(term) ||
        v.modelo?.toLowerCase().includes(term)
      );
    }

    // Filters
    if (statusFilter) result = result.filter(v => v.status === statusFilter);
    if (marcaFilter) result = result.filter(v => v.marca === marcaFilter);
    if (modeloFilter) result = result.filter(v => v.modelo === modeloFilter);
    if (anoFilter) result = result.filter(v => String(v.ano_modelo) === anoFilter);
    if (corFilter) result = result.filter(v => v.cor === corFilter);

    // Sort alphanumerically by vehicle_id (01, 01.1, 02, 02.1, 03, ...)
    result.sort((a, b) => {
      const parseId = (id) => {
        if (!id) return [0, 0];
        const parts = String(id).split('.');
        const main = parseInt(parts[0].replace(/\D/g, '')) || 0;
        const sub = parts[1] ? parseInt(parts[1]) : 0;
        return [main, sub];
      };
      
      const [aMain, aSub] = parseId(a.vehicle_id);
      const [bMain, bSub] = parseId(b.vehicle_id);
      
      if (aMain !== bMain) return aMain - bMain;
      return aSub - bSub;
    });

    setFilteredVehicles(result);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setMarcaFilter('');
    setModeloFilter('');
    setAnoFilter('');
    setCorFilter('');
    setOcultarVendidos(true);
  };

  // Funções de seleção de veículos
  const toggleVehicleSelection = (vehicleId) => {
    const newSelection = new Set(selectedVehicles);
    if (newSelection.has(vehicleId)) {
      newSelection.delete(vehicleId);
    } else {
      newSelection.add(vehicleId);
    }
    setSelectedVehicles(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedVehicles.size === filteredVehicles.length) {
      // Desmarcar todos
      setSelectedVehicles(new Set());
    } else {
      // Selecionar todos os filtrados
      const allIds = new Set(filteredVehicles.map(v => v.vehicle_id));
      setSelectedVehicles(allIds);
    }
  };

  const isAllSelected = filteredVehicles.length > 0 && selectedVehicles.size === filteredVehicles.length;
  const isSomeSelected = selectedVehicles.size > 0 && selectedVehicles.size < filteredVehicles.length;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Calculate totals - usar apenas veículos selecionados se houver seleção
  const vehiclesToCount = selectedVehicles.size > 0 
    ? filteredVehicles.filter(v => selectedVehicles.has(v.vehicle_id))
    : filteredVehicles;
  
  const totalVehicles = vehiclesToCount.length;
  const totalFIPE = vehiclesToCount.reduce((acc, v) => acc + (v.valor_fipe || 0), 0);

  const exportToCSV = () => {
    const dataToExport = vehiclesToCount; // Usa apenas selecionados ou todos
    const headers = ['ID Veículo', 'Placa', 'Marca', 'Modelo', 'Ano/Modelo', 'Valor FIPE'];
    const rows = dataToExport.map(v => [
      v.vehicle_id,
      v.placa,
      v.marca,
      v.modelo,
      `${v.ano_fabricacao}/${v.ano_modelo}`,
      v.valor_fipe || 0
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `relatorio_veiculos_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('CSV exportado!');
  };

  const exportToPDF = () => {
    window.print();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6" data-testid="vehicles-report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 font-[Manrope]">Relatório de Veículos</h1>
          <p className="text-gray-500 text-sm">Listagem completa da frota</p>
        </div>
        
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={exportToCSV}>
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button variant="outline" size="sm" onClick={exportToPDF}>
            <FileText className="w-4 h-4 mr-1" />
            PDF
          </Button>
          <Button size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-1" />
            Imprimir
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-white border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total de Veículos</p>
              <p className="text-3xl font-bold text-blue-600">{totalVehicles}</p>
              <p className="text-xs text-gray-400">
                {selectedVehicles.size > 0 
                  ? `${selectedVehicles.size} selecionado(s) de ${filteredVehicles.length} filtrado(s)`
                  : 'Veículos filtrados'}
              </p>
            </div>
            <RefreshCw className="w-8 h-8 text-blue-200" />
          </CardContent>
        </Card>
        
        <Card className="bg-white border-l-4 border-l-green-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Valor Total da Frota (FIPE)</p>
              <p className="text-3xl font-bold text-green-600">{formatCurrency(totalFIPE)}</p>
              <p className="text-xs text-gray-400">
                {selectedVehicles.size > 0 
                  ? 'Soma dos veículos selecionados'
                  : 'Soma do valor FIPE dos veículos filtrados'}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-200" />
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="print:hidden">
        <CardContent className="p-4">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Buscar por ID, Placa, Marca ou Modelo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="vehicle-search"
            />
          </div>
          
          {/* Checkbox */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="ocultarVendidos"
                checked={ocultarVendidos}
                onCheckedChange={setOcultarVendidos}
              />
              <Label htmlFor="ocultarVendidos" className="text-sm cursor-pointer">
                Ocultar veículos vendidos (marque para mostrar apenas veículos ativos)
              </Label>
            </div>
            
            {selectedVehicles.size > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-blue-700 font-medium">
                    {selectedVehicles.size} veículo(s) selecionado(s) para apresentação
                  </span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedVehicles(new Set())}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                >
                  Limpar seleção
                </Button>
              </div>
            )}
          </div>
          
          {/* Filter dropdowns */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <span>▼</span> Status
              </Label>
              <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="disponivel">Disponível</SelectItem>
                  <SelectItem value="alugado">Alugado</SelectItem>
                  <SelectItem value="manutencao">Manutenção</SelectItem>
                  <SelectItem value="reservado">Reservado</SelectItem>
                  <SelectItem value="vendido">Vendido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <span>▼</span> Marca
              </Label>
              <Select value={marcaFilter || 'all'} onValueChange={(v) => setMarcaFilter(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {marcas.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <span>▼</span> Modelo
              </Label>
              <Select value={modeloFilter || 'all'} onValueChange={(v) => setModeloFilter(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {modelos.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <span>▼</span> Ano/Modelo
              </Label>
              <Select value={anoFilter || 'all'} onValueChange={(v) => setAnoFilter(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {anos.map(a => (
                    <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <span>▼</span> Cor
              </Label>
              <Select value={corFilter || 'all'} onValueChange={(v) => setCorFilter(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {cores.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Limpar Todos os Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum veículo encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-12">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Selecionar todos"
                        className={isSomeSelected ? "data-[state=checked]:bg-blue-400" : ""}
                      />
                    </TableHead>
                    <TableHead className="font-semibold">ID Veículo</TableHead>
                    <TableHead className="font-semibold">Placa</TableHead>
                    <TableHead className="font-semibold">Marca</TableHead>
                    <TableHead className="font-semibold">Modelo</TableHead>
                    <TableHead className="font-semibold">Ano/Modelo</TableHead>
                    <TableHead className="font-semibold text-right">Valor FIPE</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVehicles.map((vehicle) => (
                    <TableRow 
                      key={vehicle.vehicle_id} 
                      className={`hover:bg-gray-50 ${selectedVehicles.has(vehicle.vehicle_id) ? 'bg-blue-50' : ''}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedVehicles.has(vehicle.vehicle_id)}
                          onCheckedChange={() => toggleVehicleSelection(vehicle.vehicle_id)}
                          aria-label={`Selecionar ${vehicle.vehicle_id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{vehicle.vehicle_id}</TableCell>
                      <TableCell>{vehicle.placa}</TableCell>
                      <TableCell>{vehicle.marca}</TableCell>
                      <TableCell>{vehicle.modelo}</TableCell>
                      <TableCell>{vehicle.ano_fabricacao}/{vehicle.ano_modelo}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(vehicle.valor_fipe)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
